import { PayloadAction, createSlice } from "@reduxjs/toolkit";

/**
 * Guided Order Entry queue slice.
 *
 * Powers the "Start Adding Orders" workflow on the Billing module: a BP picks
 * (or auto-collects) a set of pending-order patients, then walks through their
 * profiles one-by-one entering orders. Submitting an order advances to the next
 * patient; "Skip" jumps ahead without entering one; "Back to Billing" exits.
 *
 * Deliberately **ephemeral** — this reducer is NOT wrapped in `persistReducer`,
 * so the queue lives in-memory only. A real browser refresh clears it (the user
 * simply restarts from the list); in-app SPA navigation between patient profiles
 * keeps it intact, which is all the workflow needs.
 */

export interface OrderQueueItem {
  /** Numeric patient id used to build the profile URL. */
  patientId: number;
  /** End-of-month billing date (YYYY-MM-DD) so the created order is dated in
   * the correct billing month rather than today. */
  billingDate: string;
  /** Display name for the banner ("Order 2 of 7 — Jane D."). */
  patientName?: string;
}

export interface IOrderQueueStateSlice {
  /** True while a guided order-entry run is in progress. */
  active: boolean;
  /** Ordered list of patients to process. */
  items: OrderQueueItem[];
  /** Index of the patient currently being processed. */
  currentIndex: number;
  /** Number of patients whose order was submitted during this run. */
  completedCount: number;
  /** Number of patients explicitly skipped during this run. */
  skippedCount: number;
}

const name = "orderQueue";

const initialState: IOrderQueueStateSlice = {
  active: false,
  items: [],
  currentIndex: 0,
  completedCount: 0,
  skippedCount: 0,
};

const orderQueueSlice = createSlice({
  name,
  initialState,
  reducers: {
    /** Begin a guided run with the given (already de-duped) patient list. */
    startOrderQueue(state, action: PayloadAction<{ items: OrderQueueItem[] }>) {
      state.active = action.payload.items.length > 0;
      state.items = action.payload.items;
      state.currentIndex = 0;
      state.completedCount = 0;
      state.skippedCount = 0;
    },
    /** Mark the current patient's order as submitted and step forward. */
    markOrderCompleted(state) {
      state.completedCount += 1;
      state.currentIndex += 1;
    },
    /** Skip the current patient without entering an order and step forward. */
    skipOrder(state) {
      state.skippedCount += 1;
      state.currentIndex += 1;
    },
    /** End the run and reset all queue state. */
    exitOrderQueue() {
      return initialState;
    },
  },
});

export const {
  startOrderQueue,
  markOrderCompleted,
  skipOrder,
  exitOrderQueue,
} = orderQueueSlice.actions;

// NOTE: exported as a plain reducer (no redux-persist wrapper) — the queue is
// intentionally in-memory only.
export default orderQueueSlice.reducer;

// ─── Selectors ────────────────────────────────────────────────────────────────

export const selectOrderQueue = (state: {
  orderQueue: IOrderQueueStateSlice;
}) => state.orderQueue;
